/**
 * Type Definitions for Admin Services
 * These serve as documentation and validation guidelines
 */

// ===== USER TYPES =====
export const USER_STATUSES = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  SUSPENDED: 'SUSPENDED',
  ACTIVE: 'ACTIVE'
};

export const USER_ROLES = {
  USER: 'USER',
  PREMIUM: 'PREMIUM',
  ADMIN: 'ADMIN',
  SUPER_ADMIN: 'SUPER_ADMIN'
};

export const USER_SUBSCRIPTION_STATUSES = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
  PENDING: 'PENDING'
};

// ===== PRICING PLAN TYPES =====
export const PLAN_TIERS = {
  PRIME: 'Prime',
  PRIME_PLUS: 'Prime+',
  ELITE: 'Elite',
  ELITE_PLUS: 'Elite+',
  SUPREME: 'Supreme',
  SUPREME_PLUS: 'Supreme+'
};

export const PLAN_FEATURES = {
  BASIC_REPORTS: 'basic_reports',
  PREMIUM_REPORTS: 'premium_reports',
  RECOMMENDATIONS: 'recommendations',
  MARKET_INSIGHTS: 'market_insights',
  PORTFOLIO_TRACKING: 'portfolio_tracking',
  EXPERT_ANALYSIS: 'expert_analysis',
  PRIORITY_SUPPORT: 'priority_support',
  CUSTOM_ALERTS: 'custom_alerts'
};

// ===== REPORT TYPES =====
export const REPORT_STATUSES = {
  ACTIVE: 'ACTIVE',
  CLOSED: 'CLOSED',
  ARCHIVED: 'ARCHIVED',
  DRAFT: 'DRAFT',
  PUBLISHED: 'PUBLISHED'
};

export const RECOMMENDATION_STATUSES = {
  ACTIVE: 'ACTIVE',
  ACHIEVED: 'ACHIEVED',
  STOPPED_OUT: 'STOPPED_OUT',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED'
};

export const MARKET_CONDITIONS = {
  BULLISH: 'BULLISH',
  BEARISH: 'BEARISH',
  SIDEWAYS: 'SIDEWAYS'
};

export const SECTOR_OUTLOOKS = {
  POSITIVE: 'POSITIVE',
  NEGATIVE: 'NEUTRAL',
  MIXED: 'MIXED'
};

export const RISK_LEVELS = {
  LEVEL_1: 1,
  LEVEL_2: 2,
  LEVEL_3: 3,
  LEVEL_4: 4,
  LEVEL_5: 5
};

export const RECOMMENDATION_ACTIONS = {
  BUY: 'BUY',
  SELL: 'SELL',
  HOLD: 'HOLD',
  AVERAGE: 'AVERAGE',
  EXIT_PARTIAL: 'EXIT_PARTIAL',
  EXIT_FULL: 'EXIT_FULL',
  PYRAMID: 'PYRAMID'
};

export const RECOMMENDATION_TYPES = {
  INITIAL: 'INITIAL',
  FOLLOW_UP: 'FOLLOW_UP',
  EXIT: 'EXIT',
  AVERAGE_DOWN: 'AVERAGE_DOWN',
  AVERAGE_UP: 'AVERAGE_UP'
};

// ===== COMPLAINT TYPES =====
export const COMPLAINT_STATUSES = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  RESOLVED: 'RESOLVED',
  CLOSED: 'CLOSED'
};

export const COMPLAINT_PRIORITIES = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  URGENT: 'URGENT'
};

export const COMPLAINT_CATEGORIES = {
  TECHNICAL: 'TECHNICAL',
  BILLING: 'BILLING',
  CONTENT: 'CONTENT',
  SERVICE: 'SERVICE',
  OTHER: 'OTHER'
};

// ===== NEWS CACHE TYPES =====
export const NEWS_STATUSES = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  ARCHIVED: 'ARCHIVED'
};

export const NEWS_CATEGORIES = {
  MARKET_NEWS: 'MARKET_NEWS',
  COMPANY_NEWS: 'COMPANY_NEWS',
  ECONOMIC_NEWS: 'ECONOMIC_NEWS',
  SECTOR_NEWS: 'SECTOR_NEWS',
  GLOBAL_NEWS: 'GLOBAL_NEWS'
};

// ===== API CONFIG TYPES =====
export const API_STATUSES = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
  MAINTENANCE: 'MAINTENANCE',
  DEPRECATED: 'DEPRECATED'
};

export const API_PROVIDERS = {
  ALPHA_QUARK: 'ALPHA_QUARK',
  TRADING_VIEW: 'TRADING_VIEW',
  NEWS_API: 'NEWS_API',
  CUSTOM: 'CUSTOM'
};

// ===== COMMON TYPES =====
export const TIMESTAMP_FIELDS = {
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
  LAST_MODIFIED_AT: 'lastModifiedAt'
};

export const AUDIT_FIELDS = {
  CREATED_BY: 'createdBy',
  LAST_MODIFIED_BY: 'lastModifiedBy',
  APPROVED_BY: 'approvedBy',
  REJECTED_BY: 'rejectedBy'
};

// ===== QUERY OPTIONS =====
export const QUERY_OPERATORS = {
  EQUAL: '==',
  NOT_EQUAL: '!=',
  GREATER_THAN: '>',
  GREATER_THAN_EQUAL: '>=',
  LESS_THAN: '<',
  LESS_THAN_EQUAL: '<=',
  IN: 'in',
  NOT_IN: 'not-in',
  ARRAY_CONTAINS: 'array-contains',
  ARRAY_CONTAINS_ANY: 'array-contains-any'
};

export const SORT_DIRECTIONS = {
  ASC: 'asc',
  DESC: 'desc'
};

// ===== RESPONSE TYPES =====
export const API_RESPONSE_STATUSES = {
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN'
};

// ===== VALIDATION RULES =====
export const VALIDATION_RULES = {
  EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  PHONE_REGEX: /^[\+]?[1-9][\d]{0,15}$/,
  PASSWORD_MIN_LENGTH: 8,
  USERNAME_MIN_LENGTH: 3,
  USERNAME_MAX_LENGTH: 30,
  COMPANY_NAME_MAX_LENGTH: 100,
  REPORT_TITLE_MAX_LENGTH: 200,
  DESCRIPTION_MAX_LENGTH: 1000
};

// ===== ERROR MESSAGES =====
export const ERROR_MESSAGES = {
  REQUIRED_FIELD: 'This field is required',
  INVALID_EMAIL: 'Please enter a valid email address',
  INVALID_PHONE: 'Please enter a valid phone number',
  PASSWORD_TOO_SHORT: 'Password must be at least 8 characters long',
  USERNAME_TOO_SHORT: 'Username must be at least 3 characters long',
  USERNAME_TOO_LONG: 'Username must be less than 30 characters',
  COMPANY_NAME_TOO_LONG: 'Company name must be less than 100 characters',
  REPORT_TITLE_TOO_LONG: 'Report title must be less than 200 characters',
  DESCRIPTION_TOO_LONG: 'Description must be less than 1000 characters',
  INVALID_STATUS: 'Invalid status value',
  INVALID_PRIORITY: 'Invalid priority value',
  INVALID_CATEGORY: 'Invalid category value',
  DOCUMENT_NOT_FOUND: 'Document not found',
  UNAUTHORIZED_ACCESS: 'Unauthorized access',
  INSUFFICIENT_PERMISSIONS: 'Insufficient permissions',
  OPERATION_FAILED: 'Operation failed',
  VALIDATION_ERROR: 'Validation error',
  NETWORK_ERROR: 'Network error',
  TIMEOUT_ERROR: 'Request timeout'
};

// ===== SUCCESS MESSAGES =====
export const SUCCESS_MESSAGES = {
  USER_CREATED: 'User created successfully',
  USER_UPDATED: 'User updated successfully',
  USER_DELETED: 'User deleted successfully',
  USER_STATUS_UPDATED: 'User status updated successfully',
  SUBSCRIPTION_ADDED: 'Subscription added successfully',
  SUBSCRIPTION_REMOVED: 'Subscription removed successfully',
  SUBSCRIPTION_UPDATED: 'Subscription updated successfully',
  BULK_UPDATE_SUCCESS: 'Bulk update completed successfully',
  DATA_EXPORTED: 'Data exported successfully',
  REPORT_CREATED: 'Report created successfully',
  REPORT_UPDATED: 'Report updated successfully',
  REPORT_DELETED: 'Report deleted successfully',
  RECOMMENDATION_ADDED: 'Recommendation added successfully',
  RECOMMENDATION_UPDATED: 'Recommendation updated successfully',
  RECOMMENDATION_DELETED: 'Recommendation deleted successfully',
  PERFORMANCE_CALCULATED: 'Performance calculated successfully'
};

// ===== COLLECTIONS =====
export const COLLECTIONS = {
  USERS: 'users',
  REPORTS: 'reports',
  PRICING_PLANS: 'pricingPlans',
  COMPLAINTS: 'complaints',
  NEWS_CACHE: 'newsCache',
  API_CONFIG: 'apiConfig',
  USER_SUBSCRIPTIONS: 'userSubscriptions',
  RECOMMENDATIONS: 'recommendations',
  MARKET_DATA: 'marketData',
  AUDIT_LOGS: 'auditLogs'
}; 